import type { UseQueryLoaderLoadQueryOptions } from 'react-relay';

import { fg } from '@atlaskit/platform-feature-flags/fg';
import type { ProductKeys3P } from '@atlassian/rovo-connectors-config/types';
import { type EntityATI } from '@atlassian/search-client/common/types';
import type { SearchCombinedUnitMetadata } from '@atlassian/search-client/config';
import {
	type TProductDataRegions,
	type TProductEdition,
} from '@atlassian/search-client/fetch-search-config';
import { getSearchUseCaseIdForRequest } from '@atlassian/search-client/use-case-id';
import { serveFpsMultiSiteSearchExperiment } from '@atlassian/search-common/experiments';
import { getCloudIdARIs } from '@atlassian/search-common/get-cloud-id-ari';
import {
	EDGE_REGION_HINT_HEADER,
	getEdgeRegionHint,
} from '@atlassian/search-common/get-edge-region-hint';
import type {
	SearchMatchingLayerExperimentsMetadata,
	GetLayerValuesFn,
} from '@atlassian/search-experiment/dynamic/types';
import type { BackendExperimentLayerOverride } from '@atlassian/search-experiment/statsig/dynamic';
import type { BackendExperiment } from '@atlassian/search-experiment/types';
import type { SearchUseCaseId } from '@atlassian/search-common/search-use-case-id';

import {
	getDefaultEntityAti,
	RESULT_LIMIT_FOR_MULTIPLE_PRODUCTS,
	RESULT_LIMIT_FOR_SINGLE_PRODUCT,
} from '../../common/constants/entities';
import { ALL_FILTER_EXCLUDED_PRODUCTS } from '../../common/constants/filters';
import {
	type ProductKeys,
	productsSupportingEmptyQuery,
	type PrimaryProductKey,
} from '../../common/constants/product-keys';
import type {
	ForgeConnectorProductKey,
	ThirdPartyConfigsWithAPIResponse,
} from '../../common/constants/schemas/3p-config';
import type { UserDetailsWithPicture } from '../../common/constants/types';
import { shouldRequestCodeResults } from '../../common/utils/code-search-mode';
import {
	serve3PClickIngestionExperiment,
	serveFastJiraHydrationExperiment,
} from '../../common/utils/experiments';
import { isHello } from '../../common/utils/is-hello';
import { isJiraProduct } from '../../common/utils/is-jira-product';
import type { QueryParams } from '../../common/utils/query-params/types';
import { getJiraFiltersForResultsCountQuery } from '../../controllers/results/utils';
import type { rightPanelQuery$variables } from '../../ui/search-page-relay/right-panel/__generated__/rightPanelQuery.graphql';
import type {
	SearchThirdPartyFilter,
	searchResultsAsyncQuery$variables,
} from '../../ui/search-page-relay/search-results/__generated__/searchResultsAsyncQuery.graphql';
import { getSessionIds } from '../../ui/search-page-relay/utils/session';

import { getSearchExperimentConfig } from './experiments';
import { getSearchFiltersConfig } from './filters';
import { getSearchInputConfig } from './input';
import { getSearchResultsQueryRelayProvidedVariables } from './relay-provided-variables';
import type { RelayProvidedVariables } from './types';

/**
 * All arguments needed to generate a search query.
 *
 * This is the raw input type — generateSearchInputs calls the individual
 * getter functions internally using these props.
 */
export type SearchArguments = {
	backendExperimentOverrides: BackendExperimentLayerOverride[] | undefined;
	cloudId: string;
	experimentLayers: string[];
	experience: 'search-page' | 'quick-find';
	useCaseId: SearchUseCaseId;
	isJiraIssueIndexed: boolean;
	jsmIncidentFilterIds: string[];
	primaryProduct: PrimaryProductKey;
	productDataRegions: TProductDataRegions;
	productEdition: TProductEdition;
	products: ProductKeys[];
	unitMetadata?: SearchCombinedUnitMetadata[];
	queryParams: QueryParams;

	/**
	 * Not guaranteed to be the same value that is used in the query. Uses a
	 * cached value when all other variables in the query are the same.
	 */
	sessionId: string | undefined;

	/**
	 * Not guaranteed to be the same value that is used in the query. Uses a
	 * cached value when all other variables in the query are the same.
	 */
	searchSessionId: string | undefined;
	thirdPartyConfigs: Partial<
		Record<ProductKeys3P | ForgeConnectorProductKey, ThirdPartyConfigsWithAPIResponse>
	>;
	user: UserDetailsWithPicture | undefined;

	onError: (error: Error) => void;

	/** Optional SSR-compatible layer value resolver — when provided, bypasses browser-only FeatureGates.getLayer() */
	getLayerValuesFn?: GetLayerValuesFn;
	expectResult?: boolean;
	/** Whether the app is in lite mode (table row layout). Used to conditionally fetch priority field. */
	isLiteMode?: boolean;
};

/**
 * Type for search results query variables.
 *
 * Kept as a type alias rather than `searchResultsAsyncQuery$variables` directly so external
 * consumers can keep their imports stable. `expectResult` is already part of the async query
 * variables.
 */
export type SearchResultsQueryVariablesWithExpectResult = searchResultsAsyncQuery$variables & {
	matchingLayerExperimentsMetadata?: SearchMatchingLayerExperimentsMetadata;
};

/**
 * Type for right panel query variables, including the optional async-only expectResult flag.
 */
export type RightPanelQueryVariablesWithExpectResult = rightPanelQuery$variables & {
	expectResult?: boolean;
};

/**
 * The output of generateSearchInputs — everything needed to run a search query.
 */
export type SearchQueryInputs = {
	searchResultsQueryVariables: SearchResultsQueryVariablesWithExpectResult;
	rightPanelQueryVariables: RightPanelQueryVariablesWithExpectResult;
	/**
	 * Relay provided variables for the searchResultsQuery. Necessary for usage
	 * of the persisted Relay query outside of a Relay environment.
	 */
	searchResultsQueryRelayProvidedVariables: RelayProvidedVariables;
	backendExperiment: BackendExperiment;
	relayFetchOptions?: UseQueryLoaderLoadQueryOptions;
};

export type SearchQueryGenerator = (args: SearchArguments) => SearchQueryInputs;

/**
 * Pure function to generate inputs required to run a searchResultsQuery and rightPanelQuery.
 *
 * SSR Safety Contract: This function and its entire call tree MUST remain:
 * - Synchronous (no async/await, no Promises)
 * - Free of React hooks (no useState, useEffect, etc.)
 * - Free of browser-only globals (no window, document, localStorage)
 *
 * This contract is enforced by tests in index.ssr-safety.test.ts.
 */
export const generateSearchInputs: SearchQueryGenerator = ({
	backendExperimentOverrides,
	cloudId,
	experimentLayers: rawExperimentLayers,
	experience,
	useCaseId,
	isJiraIssueIndexed,
	jsmIncidentFilterIds,
	primaryProduct,
	productDataRegions,
	productEdition,
	products: bootstrapProducts,
	unitMetadata,
	queryParams,
	sessionId: rawSessionId,
	searchSessionId: rawSearchSessionId,
	thirdPartyConfigs,
	user,
	onError,
	getLayerValuesFn,
	expectResult = true,
	isLiteMode = false,
}) => {
	const { originalQuery, searchQuery } = getSearchInputConfig({ queryParams });

	const {
		availableProducts: products,
		entities,
		entitiesForResultsCountQuery,
		selectedEntities,
		selectedFilters,
		selectedProducts,
		sites,
		unselectedProductsThirdPartyFilters,
	} = getSearchFiltersConfig({
		allFilterExcludedProducts: ALL_FILTER_EXCLUDED_PRODUCTS,
		availableProducts: bootstrapProducts,
		cloudId,
		currentUserId: user?.id,
		jsmIncidentFilterIds,
		primaryProduct,
		queryParams,
		thirdPartyConfigs,
		...(experience === 'search-page' && unitMetadata ? { unitMetadata } : {}),
	});
	const searchableProducts = getSearchableProducts(searchQuery, products);
	const {
		assetsFilters,
		commonFilters,
		compassFilters,
		confluenceFilters,
		jiraFilters,
		loomFilters,
		mercuryFilters,
		sortFilters,
		talentFilters,
		thirdPartyFilters,
	} = selectedFilters;

	const {
		experimentId,
		experimentLayers,
		matchingLayerExperimentsMetadata,
		shadowExperimentId,
		fanoutExperimentCohort,
		backendExperiment,
	} = getSearchExperimentConfig({
		backendExperimentOverrides,
		cloudId,
		entities,
		experimentLayers: rawExperimentLayers,
		experienceLayer: experience,
		isHello: isHello(cloudId),
		isJiraIssueIndexed,
		onError,
		productDataRegions,
		productEdition,
		thirdPartyFilters: selectedFilters.thirdPartyFilters,
		getLayerValuesFn,
	});

	const requestHeaders = {
		[EDGE_REGION_HINT_HEADER]:
			primaryProduct && productDataRegions
				? getEdgeRegionHint(primaryProduct, productDataRegions)
				: undefined,
	};

	const additionalSites = serveFpsMultiSiteSearchExperiment({
		primaryProduct,
		fireExperimentExposure: false,
	})
		? sites
		: [];
	const cloudIdARIs = getCloudIdARIs(
		cloudId,
		primaryProduct,
		additionalSites, // For multi-site search queries
	);
	const requestUseCaseId = getSearchUseCaseIdForRequest(useCaseId);

	// Fast Jira value-field hydration experiment. Evaluate the experiment ONCE so we only fire a
	// single exposure event.
	const hasJiraProduct = products.some((product) => isJiraProduct(product, true));
	const fastJiraHydrationEnabled = hasJiraProduct && serveFastJiraHydrationExperiment();

	const sharedQueryVariables = {
		commonFilters,
		compassFilters,
		confluenceFilters,
		disableQueryReplacement: !!originalQuery,
		entities,
		experience,
		...(requestUseCaseId ? { useCaseId: requestUseCaseId } : {}),
		jiraFilters,
		limit: getLimit(entities),
		mercuryFilters,
		query: searchQuery,
		queryVersion: 1,
		searchSessionId: rawSearchSessionId,
		sessionId: rawSessionId,
		sort: sortFilters,
		sourceProduct: primaryProduct,
		tenantId: cloudId,
		thirdPartyFilters: thirdPartyFilters as unknown as SearchThirdPartyFilter,
		...(fg('assets_rovo_search_all') ? { assetsFilters } : {}),
		...(fg('talent_global_search') ? { talentFilters } : {}),
	};
	const shouldIncludeLoomFilters =
		entities.some((entity) => entity.startsWith('ati:cloud:loom:')) &&
		Object.values(loomFilters).some((value) => value && value.length > 0);

	const searchResultsQueryVariables: SearchResultsQueryVariablesWithExpectResult = {
		...sharedQueryVariables,
		...(shouldIncludeLoomFilters ? { loomFilters } : {}),
		cloudIdARIs,
		expectResult,
		/**
		 * Highlighting is only useful for results
		 */
		enableHighlighting: true,
		/**
		 * Experimentation is only valid in results section. Note that experiments
		 * can differ based on selected entites/products, so if we include experiments
		 * in counts, the counts will change as filters change
		 */
		experimentId,
		experimentLayers: experimentLayers?.map(({ name, layerId, shadowId, definitions }) => ({
			name,
			layerId,
			shadowId,
			definitions,
		})),
		...(matchingLayerExperimentsMetadata ? { matchingLayerExperimentsMetadata } : {}),
		fanoutExperimentCohort,
		includeCodeSearch: shouldRequestCodeResults(selectedProducts, selectedEntities),
		includeJiraLiteModeFields:
			isLiteMode && selectedProducts.some((product) => isJiraProduct(product, true)),
		// Fast Jira value-field hydration experiment. Enrolled whenever a Jira product is among
		// the products being searched (`products` includes selected-site products and drives the
		// entities). We do NOT use
		// the filter-selection `selectedProducts` here: that is empty on the default "All apps"
		// search, which would wrongly skip enrolment even though Jira results are shown (e.g. when
		// searching Jira from Confluence). When on, the query selects the lighter
		// issueType/assignee/project/priority/labels value fields instead of the higher-cost
		// *Field variants (see searchJiraIssue_AtlassianSearchPage).
		includeFastJiraHydration: fastJiraHydrationEnabled,
		includeNavBoostScore: fg('kd_enable_nav_boost_score'),
		includePassionfruitAsk: fg('platform_passionfruit_search_onboarding_enabled'),
		includeRisk: fg('platform_mercury_risk_search_ui_enabled'),
		includePositions: fg('talent_global_search'),
		includeThirdPartyId: serve3PClickIngestionExperiment(thirdPartyConfigs, {
			fireExperimentExposure: false,
		}),
		includeReactionsAndCommentCount: fg('rovo_fps_graph_like_comment_count'),
		includeCaseEmbeddedComments: fg('rovo_fps_case_embedded_comments'),
		shadowExperimentId,
		thirdPartyFilters: thirdPartyFilters as unknown as SearchThirdPartyFilter,
	};

	const rightPanelQueryVariables: RightPanelQueryVariablesWithExpectResult = {
		...sharedQueryVariables,
		cloudIdARIs,
		expectResult,
		/**
		 * Counts searches for approx all entities (see getSelectedFilters for edge cases)
		 */
		entities: entitiesForResultsCountQuery,
		/**
		 * We need to search for all Jira product types to support counts in Jira/JSM/JPD correctly
		 */
		jiraFilters: getJiraFiltersForResultsCountQuery(jiraFilters, searchableProducts),
		limit: 0,
		sort: [],
		thirdPartyFilters: unselectedProductsThirdPartyFilters as unknown as SearchThirdPartyFilter,
	};

	/**
	 * Relay uses the entire variables objects as a cache key. For this reason,
	 * when making the same search request, we need to ensure we retrieve the
	 * same sessionId and searchSessionId. We do this by caching these ids
	 * and retrieving them using the variables object excluding the ids as a cache key.
	 */
	const [searchResultsQuerySessionId, searchResultsQuerySearchSessionId] = getSessionIds(
		searchResultsQueryVariables,
	);
	searchResultsQueryVariables.sessionId = searchResultsQuerySessionId;
	searchResultsQueryVariables.searchSessionId = searchResultsQuerySearchSessionId;
	const [rightPanelQuerySessionId, rightPanelQuerySearchSessionId] =
		getSessionIds(rightPanelQueryVariables);
	rightPanelQueryVariables.sessionId = rightPanelQuerySessionId;
	rightPanelQueryVariables.searchSessionId = rightPanelQuerySearchSessionId;

	const searchResultsQueryRelayProvidedVariables: RelayProvidedVariables =
		getSearchResultsQueryRelayProvidedVariables();

	return {
		backendExperiment,
		requestHeaders,
		rightPanelQueryVariables,
		searchResultsQueryRelayProvidedVariables,
		searchResultsQueryVariables,
	};
};

const getLimit = (entities: EntityATI[]) => {
	return entities.length === getDefaultEntityAti().length
		? RESULT_LIMIT_FOR_MULTIPLE_PRODUCTS
		: RESULT_LIMIT_FOR_SINGLE_PRODUCT;
};

const getSearchableProducts = (searchQuery: string, products: ProductKeys[]) => {
	const isEmptyQuery = !searchQuery || searchQuery.trim() === '';
	return products.filter(
		(product) => !isEmptyQuery || productsSupportingEmptyQuery.includes(product),
	);
};
